import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import AdminPanel from "./components/AdminPanel";
import ServiceInterface from "./components/ServiceInterface";
import AdminLogin from "./components/AdminLogin";
import { Button } from "./components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "./components/ui/card";
import { Users, CreditCard, Settings } from "lucide-react";
import { Toaster } from "sonner";
import { LanguageProvider, useTranslation } from "./lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = React.createContext();

const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [admin, setAdmin] = useState(null);

  const login = (token, adminData) => {
    setToken(token);
    setAdmin(adminData);
    localStorage.setItem('admin_token', token);
  };

  const logout = () => {
    setToken(null);
    setAdmin(null);
    localStorage.removeItem('admin_token');
  };

  return (
    <AuthContext.Provider value={{ token, admin, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const Navigation = () => {
  const location = useLocation();
  const { admin, logout } = useAuth();
  const { t, language, setLanguage } = useTranslation();

  // Simple, always visible navigation
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      height: '80px',
      backgroundColor: '#1e40af',
      color: 'white',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 20px',
      fontSize: '18px',
      fontWeight: 'bold'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ fontSize: '24px', marginRight: '10px' }}>🏢</span>
          <span>{t("navigation.brand")}</span>
        </div>

        <div style={{ display: 'flex', gap: '15px' }}>
          {/* Only show admin links for admin users */}
          {admin?.role === 'admin' && (
            <>
              <Link 
                to="/" 
                style={{
                  padding: '10px 20px',
                  backgroundColor: location.pathname === '/' ? 'white' : 'transparent',
                  color: location.pathname === '/' ? '#1e40af' : 'white',
                  textDecoration: 'none',
                  borderRadius: '5px',
                  border: '2px solid white'
                }}
              >
                🏠 {t("navigation.home")}
              </Link>

              <Link
                to="/admin"
                style={{
                  padding: '10px 20px',
                  backgroundColor: location.pathname === '/admin' ? 'white' : 'transparent',
                  color: location.pathname === '/admin' ? '#1e40af' : 'white',
                  textDecoration: 'none',
                  borderRadius: '5px',
                  border: '2px solid white'
                }}
              >
                👤 {t("navigation.admin")}
              </Link>
            </>
          )}

          {/* Card reader accessible to both admin and cashier */}
          <Link 
            to="/service"
            style={{
              padding: '10px 20px',
              backgroundColor: location.pathname === '/service' ? 'white' : 'transparent',
              color: location.pathname === '/service' ? '#1e40af' : 'white',
              textDecoration: 'none',
              borderRadius: '5px',
              border: '2px solid white'
            }}
          >
            💳 {t("navigation.service")}
          </Link>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label htmlFor="language-select" style={{ fontSize: '14px', fontWeight: 'normal' }}>
            {t("language.label")}:
          </label>
          <select
            id="language-select"
            value={language}
            onChange={(event) => setLanguage(event.target.value)}
            style={{
              padding: '6px 10px',
              borderRadius: '5px',
              border: '1px solid rgba(255,255,255,0.7)',
              backgroundColor: 'rgba(255,255,255,0.15)',
              color: 'white',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            <option value="en">{t("language.english")}</option>
            <option value="tr">{t("language.turkish")}</option>
          </select>
        </div>
        <span>
          {t("navigation.welcome", {
            name: admin?.username || t("navigation.user"),
            role: admin?.role === 'admin' ? t("roles.admin") : t("roles.cashier")
          })}
        </span>
        <button
          onClick={logout}
          style={{
            padding: '10px 20px',
            backgroundColor: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
        >
          🚪 {t("navigation.logout")}
        </button>
      </div>
    </div>
  );
};

const HomePage = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t("home.title")}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t("home.subtitle")}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <Card className="hover:shadow-lg transition-shadow duration-300">
            <CardHeader>
              <CardTitle className="flex items-center text-blue-700">
                <Users className="h-6 w-6 mr-3" />
                {t("home.adminPanelTitle")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                {t("home.adminPanelDescription")}
              </p>
              <Link to="/admin">
                <Button className="w-full bg-blue-600 hover:bg-blue-700">
                  {t("home.adminPanelCta")}
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow duration-300">
            <CardHeader>
              <CardTitle className="flex items-center text-green-700">
                <CreditCard className="h-6 w-6 mr-3" />
                {t("home.serviceTitle")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                {t("home.serviceDescription")}
              </p>
              <Link to="/service">
                <Button className="w-full bg-green-600 hover:bg-green-700">
                  {t("home.serviceCta")}
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        <div className="bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">{t("home.featuresTitle")}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-blue-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <CreditCard className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t("home.features.prepaidTitle")}</h3>
              <p className="text-gray-600 text-sm">{t("home.features.prepaidDescription")}</p>
            </div>
            <div className="text-center">
              <div className="bg-green-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Settings className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t("home.features.flexibleTitle")}</h3>
              <p className="text-gray-600 text-sm">{t("home.features.flexibleDescription")}</p>
            </div>
            <div className="text-center">
              <div className="bg-purple-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Users className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t("home.features.unlimitedTitle")}</h3>
              <p className="text-gray-600 text-sm">{t("home.features.unlimitedDescription")}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const { token } = useAuth();
  return token ? children : <Navigate to="/login" replace />;
};

const AdminRoute = ({ children }) => {
  const { admin } = useAuth();
  return admin?.role === 'admin' ? children : <Navigate to="/service" replace />;
};

function App() {
  return (
    <div className="App min-h-screen bg-gray-50">
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<AdminLogin />} />
              <Route path="/*" element={
                <ProtectedRoute>
                  <>
                    <Navigation />
                    <div style={{ paddingTop: '100px' }}> {/* Simple padding for the fixed nav */}
                      <Routes>
                        <Route path="/" element={<AdminRoute><HomePage /></AdminRoute>} />
                        <Route path="/admin" element={<AdminRoute><AdminPanel /></AdminRoute>} />
                        <Route path="/service" element={<ServiceInterface />} />
                      </Routes>
                    </div>
                  </>
                </ProtectedRoute>
              } />
            </Routes>
            <Toaster position="top-right" />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
export { useAuth };